import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import {
    OrderRecheckParams,
    OrderRecheckResponse,
} from '../types/orderRecheckSchema';

import type { ThunkConfig } from 'app/providers/StoreProvider/config/types';

const RECHECK_POLLING_INTERVAL = 2000; // 2 seconds
const MAX_RECHECK_POLLING_TIME = 60000; // 60 seconds max

/**
 * Starts the recheck process for an order
 * Returns 204 No Content on success
 */
export const startOrderRecheck = createAsyncThunk<
    void,
    OrderRecheckParams,
    ThunkConfig<string>
>('order/startRecheck', async ({ orderId }, thunkApi) => {
    const { extra, rejectWithValue } = thunkApi;

    try {
        await extra.api.post(`/v1/orders/${orderId}/recheck`);
        // POST returns 204 No Content, so no data to return
    } catch (e: unknown) {
        const error = e as AxiosError<{ detail?: string }>;
        if (error.response?.status === 400) {
            return rejectWithValue(
                error.response.data?.detail ||
                    'Order cannot be rechecked at this stage',
            );
        }
        return rejectWithValue(
            error.response?.data?.detail || 'Failed to start recheck',
        );
    }
});

/**
 * Polls for recheck results until they are available
 * Returns 404 while checks are in progress, 200 with data when complete
 */
export const pollForOrderRecheckResults = createAsyncThunk<
    OrderRecheckResponse,
    OrderRecheckParams,
    ThunkConfig<string>
>('order/getRecheckResults', async ({ orderId }, thunkApi) => {
    const { extra, rejectWithValue } = thunkApi;

    const startTime = Date.now();

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime >= MAX_RECHECK_POLLING_TIME) {
            return rejectWithValue('Recheck failure - timeout');
        }

        try {
            const response = await extra.api.get<OrderRecheckResponse>(
                `/v1/orders/${orderId}/recheck`,
            );
            return response.data;
        } catch (e: unknown) {
            const error = e as AxiosError<{ detail?: string }>;
            if (error.response?.status === 404) {
                // Still in progress, wait and retry
                await new Promise((resolve) =>
                    setTimeout(resolve, RECHECK_POLLING_INTERVAL),
                );
            } else {
                return rejectWithValue(
                    error.response?.data?.detail ||
                        'Recheck failure - could not get recheck results',
                );
            }
        }
    }
});

/**
 * Starts order recheck and polls for results
 */
export const runOrderRecheck = createAsyncThunk<
    OrderRecheckResponse,
    OrderRecheckParams,
    ThunkConfig<string>
>('order/runRecheck', async (params, thunkApi) => {
    const { dispatch, rejectWithValue } = thunkApi;

    const startResult = await dispatch(startOrderRecheck(params));
    if (startOrderRecheck.rejected.match(startResult)) {
        return rejectWithValue(startResult.payload as string);
    }

    const resultsResult = await dispatch(pollForOrderRecheckResults(params));
    if (pollForOrderRecheckResults.rejected.match(resultsResult)) {
        return rejectWithValue(resultsResult.payload as string);
    }

    return resultsResult.payload;
});
